// ============================================================================
// HOLIDAY BULK ADD MODAL
// ============================================================================
// modal-lg — Bulk holiday entry with an inline editable grid.
// Users select a year, one or more offices, then add holiday rows
// (date + description + type) in a table. "Copy from Previous Year" copies
// holidays from the prior year. Save All creates one record per
// (office x holiday row) via holidayService.createBulk().
//
// This is NOT a standard form modal — it has custom inline-grid UX.

import React, { useState, useCallback, useRef, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { holidayService } from 'api/services/holidayService';
import notificationService from 'services/notificationService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HolidayRow {
  id: string;
  date: string;
  description: string;
  holidayType: string;
}

export interface OfficeOptionWithGeo {
  value: string;
  label: string;
  countryId?: string;
  countryName?: string;
  stateId?: string;
  stateName?: string;
}

export interface HolidayBulkAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveComplete: () => void;
  officeOptions: OfficeOptionWithGeo[];
  countryOptions: { value: string; label: string }[];
  stateOptions: { value: string; label: string }[];
  onCountryChange?: (countryId: string) => void;
  // Minimize support
  tabId?: string;
  moduleName?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CURRENT_YEAR = new Date().getFullYear();

const YEAR_OPTIONS: { value: string; label: string }[] = Array.from(
  { length: 11 },
  (_, i) => {
    const y = CURRENT_YEAR - 5 + i;
    return { value: String(y), label: String(y) };
  },
);

const HOLIDAY_TYPE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'NATIONAL', label: 'National' },
  { value: 'REGIONAL', label: 'Regional' },
  { value: 'RELIGIOUS', label: 'Religious' },
  { value: 'COMPANY', label: 'Company' },
];

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let rowCounter = 0;
const nextRowId = (): string => `row-${++rowCounter}`;

/** Take month+day from source date, set year to target year. */
const adjustDateToYear = (dateStr: string, targetYear: number): string => {
  const parts = dateStr.substring(0, 10).split('-');
  if (parts.length < 3) return dateStr;
  return `${targetYear}-${parts[1]}-${parts[2]}`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const HolidayBulkAddModal: React.FC<HolidayBulkAddModalProps> = ({
  isOpen,
  onClose,
  onSaveComplete,
  officeOptions,
  countryOptions,
  stateOptions,
  onCountryChange,
  tabId,
  moduleName,
  minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  // --- Country/State filter for offices ---
  const [countryFilter, setCountryFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');

  const filteredOfficeOptions = officeOptions.filter((o) => {
    if (countryFilter && o.countryId !== countryFilter) return false;
    if (stateFilter && o.stateId !== stateFilter) return false;
    return true;
  });

  // --- State ---
  const [year, setYear] = useState<number>(CURRENT_YEAR);
  const [selectedOfficeIds, setSelectedOfficeIds] = useState<string[]>([]);
  const [holidayRows, setHolidayRows] = useState<HolidayRow[]>([
    { id: nextRowId(), date: '', description: '', holidayType: '' },
  ]);
  const [statusCode, setStatusCode] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [isSaving, setIsSaving] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Refs for minimize
  const yearRef = useRef(year);
  yearRef.current = year;
  const selectedOfficeIdsRef = useRef(selectedOfficeIds);
  selectedOfficeIdsRef.current = selectedOfficeIds;
  const holidayRowsRef = useRef(holidayRows);
  holidayRowsRef.current = holidayRows;
  const statusCodeRef = useRef(statusCode);
  statusCodeRef.current = statusCode;
  const isRestoreRef = useRef(false);

  // --- Reset on open ---
  React.useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      return;
    }
    setYear(CURRENT_YEAR);
    setSelectedOfficeIds([]);
    setHolidayRows([{ id: nextRowId(), date: '', description: '', holidayType: '' }]);
    setStatusCode('ACTIVE');
    setFieldErrors({});
  }, [isOpen]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const savedYear = yearRef.current;
    const savedOffices = [...selectedOfficeIdsRef.current];
    const savedRows = holidayRowsRef.current.map((r) => ({ ...r }));
    const savedStatus = statusCodeRef.current;

    const modalId = `${tabId}-bulk-add-holidays`;
    const title = `Bulk Add Holidays - ${savedYear}`;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: savedRows.some((r) => r.date || r.description),
      onRestore: () => {
        isRestoreRef.current = true;
        setYear(savedYear);
        setSelectedOfficeIds(savedOffices);
        setHolidayRows(savedRows);
        setStatusCode(savedStatus);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Row manipulation ---
  const addRow = useCallback(() => {
    setHolidayRows((prev) => [
      ...prev,
      { id: nextRowId(), date: '', description: '', holidayType: '' },
    ]);
  }, []);

  const removeRow = useCallback((id: string) => {
    setHolidayRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((r) => r.id !== id);
    });
  }, []);

  const updateRow = useCallback(
    (id: string, field: keyof Omit<HolidayRow, 'id'>, value: string) => {
      setHolidayRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
      );
      // Clear row-level errors
      setFieldErrors((prev) => {
        const key = `${id}-${field}`;
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  // --- Copy from previous year ---
  const handleCopyFromPrevYear = useCallback(async () => {
    if (selectedOfficeIds.length === 0) return;
    setIsCopying(true);
    try {
      const response = await holidayService.getAll({
        year: year - 1,
        officeId: selectedOfficeIds[0],
        pageSize: 100,
        includeInactive: false,
      });
      const items = response.data;
      if (items.length === 0) {
        notificationService.toast.info(`No holidays found for ${year - 1}.`);
        setIsCopying(false);
        return;
      }
      const newRows: HolidayRow[] = items.map((h) => ({
        id: nextRowId(),
        date: adjustDateToYear(h.holidayDate, year),
        description: h.description,
        holidayType: h.holidayType || '',
      }));
      setHolidayRows((prev) => [...prev, ...newRows]);
      notificationService.toast.success(`Copied ${items.length} holidays from ${year - 1}.`);
    } catch {
      notificationService.toast.error('Failed to copy holidays from previous year.');
    } finally {
      setIsCopying(false);
    }
  }, [year, selectedOfficeIds]);

  // --- Office multi-select ---
  const handleOfficeToggle = useCallback((officeId: string) => {
    setSelectedOfficeIds((prev) =>
      prev.includes(officeId)
        ? prev.filter((id) => id !== officeId)
        : [...prev, officeId],
    );
    setFieldErrors((prev) => {
      if (!prev.offices) return prev;
      const next = { ...prev };
      delete next.offices;
      return next;
    });
  }, []);

  const handleSelectAllOffices = useCallback(() => {
    const filteredIds = filteredOfficeOptions.map((o) => o.value);
    setSelectedOfficeIds((prev) => {
      const allSelected = filteredIds.every((id) => prev.includes(id));
      if (allSelected) {
        // Deselect only the filtered ones, keep others
        return prev.filter((id) => !filteredIds.includes(id));
      }
      // Add all filtered, keep existing selections from other filters
      return [...new Set([...prev, ...filteredIds])];
    });
    setFieldErrors((prev) => {
      if (!prev.offices) return prev;
      const next = { ...prev };
      delete next.offices;
      return next;
    });
  }, [officeOptions]);

  // --- Validation ---
  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (selectedOfficeIds.length === 0) {
      errors.offices = 'Select at least one location';
    }

    const nonEmptyRows = holidayRows.filter((r) => r.date || r.description);
    if (nonEmptyRows.length === 0) {
      errors.rows = 'Add at least one holiday';
    }

    const seenDates = new Set<string>();
    for (const row of holidayRows) {
      if (!row.date && !row.description) continue; // skip completely empty rows
      if (!row.date) {
        errors[`${row.id}-date`] = 'Date required';
      } else {
        const rowYear = parseInt(row.date.substring(0, 4), 10);
        if (rowYear !== year) {
          errors[`${row.id}-date`] = `Year must be ${year}`;
        }
        if (seenDates.has(row.date)) {
          errors[`${row.id}-date`] = 'Duplicate date';
        }
        seenDates.add(row.date);
      }
      if (!row.description.trim()) {
        errors[`${row.id}-description`] = 'Description required';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [selectedOfficeIds, holidayRows, year]);

  // --- Save All ---
  const handleSaveAll = useCallback(async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      const validRows = holidayRows.filter((r) => r.date && r.description.trim());
      const requests = selectedOfficeIds.flatMap((officeId) =>
        validRows.map((row) => ({
          year,
          officeId,
          holidayDate: row.date,
          description: row.description.trim(),
          holidayType: row.holidayType || undefined,
          statusCode,
        })),
      );

      const result = await holidayService.createBulk(requests);
      notificationService.toast.success(
        `Created ${result.success} holidays${result.failed > 0 ? `, ${result.failed} failed` : ''}.`,
      );

      if (result.success > 0) {
        onSaveComplete();
        onClose();
      }
    } catch {
      notificationService.toast.error('Failed to create holidays. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [validate, holidayRows, selectedOfficeIds, year, statusCode, onSaveComplete, onClose]);

  // --- Close handler ---
  const handleClose = useCallback(() => {
    if (isSaving) return;
    onClose();
  }, [isSaving, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {/* Loading overlay */}
        {isSaving && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 'inherit',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem',
                height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving...
            </span>
          </div>
        )}

        {/* Header */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Bulk Add Holidays</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button
              className="modal-close"
              onClick={handleClose}
              disabled={isSaving}
              title="Close"
            >
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {/* Row 1: Year + Status */}
          <div className="form-grid-2">
            <div className="form-group">
              <SyutSelect
                label="Year"
                value={String(year)}
                options={YEAR_OPTIONS}
                isClearable={false}
                size="sm"
                onChange={(val) => setYear(parseInt(val as string, 10) || CURRENT_YEAR)}
              />
            </div>
            <div className="form-group">
              <SyutSelect
                label="Status"
                value={statusCode}
                options={STATUS_OPTIONS}
                isClearable={false}
                size="sm"
                onChange={(val) =>
                  setStatusCode((val as 'ACTIVE' | 'INACTIVE') || 'ACTIVE')
                }
              />
            </div>
          </div>

          {/* Country/State filter for locations */}
          <div className="form-grid-2">
            <div className="form-group">
              <SyutSelect
                label="Country"
                value={countryFilter || undefined}
                options={[{ value: '', label: 'All Countries' }, ...countryOptions]}
                isClearable
                size="sm"
                placeholder="Filter by country..."
                onChange={(val) => {
                  const newCountry = (val as string) || '';
                  setCountryFilter(newCountry);
                  setStateFilter('');
                  onCountryChange?.(newCountry);
                }}
              />
            </div>
            <div className="form-group">
              <SyutSelect
                label="State / Province"
                value={stateFilter || undefined}
                options={[{ value: '', label: 'All States' }, ...stateOptions]}
                isClearable
                size="sm"
                placeholder={countryFilter ? 'Filter by state...' : 'Select country first'}
                onChange={(val) => setStateFilter((val as string) || '')}
              />
            </div>
          </div>

          {/* Location multi-select */}
          <div className="form-group" data-field="offices">
            <label className="form-label">
              Locations <span className="text-danger">*</span>
            </label>
            <div
              style={{
                border: `1px solid ${fieldErrors.offices ? '#ef4444' : 'var(--form-border-color)'}`,
                borderRadius: '4px',
                maxHeight: '140px',
                overflow: 'auto',
                padding: '0.25rem',
              }}
            >
              {/* Select All */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--border-color)',
                  marginBottom: '0.125rem',
                }}
              >
                <input
                  type="checkbox"
                  checked={filteredOfficeOptions.length > 0 && filteredOfficeOptions.every((o) => selectedOfficeIds.includes(o.value))}
                  onChange={handleSelectAllOffices}
                  disabled={isSaving}
                />
                Select All ({filteredOfficeOptions.length})
              </label>
              {filteredOfficeOptions.map((opt) => (
                <label
                  key={opt.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.2rem 0.5rem',
                    fontSize: '0.8125rem',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedOfficeIds.includes(opt.value)}
                    onChange={() => handleOfficeToggle(opt.value)}
                    disabled={isSaving}
                  />
                  {opt.label}
                </label>
              ))}
              {officeOptions.length === 0 && (
                <div style={{ padding: '0.5rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                  No locations available
                </div>
              )}
            </div>
            {fieldErrors.offices && (
              <div className="form-error-message">{fieldErrors.offices}</div>
            )}
            {selectedOfficeIds.length > 0 && (
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                {selectedOfficeIds.length} location(s) selected
              </div>
            )}
          </div>

          {/* Copy from previous year button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <button
              className="btn btn-secondary"
              onClick={handleCopyFromPrevYear}
              disabled={isSaving || isCopying || selectedOfficeIds.length === 0}
              style={{ fontSize: '0.8125rem' }}
            >
              {isCopying ? 'Copying...' : `Copy from ${year - 1}`}
            </button>
            {fieldErrors.rows && (
              <span style={{ fontSize: '0.8125rem', color: '#ef4444' }}>{fieldErrors.rows}</span>
            )}
          </div>

          {/* Holiday rows inline grid */}
          <div
            style={{
              border: '1px solid var(--form-border-color)',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '300px',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr
                  style={{
                    background: 'var(--grid-header-bg, #dbeafe)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <th style={{ padding: '0.5rem 0.625rem', textAlign: 'left', fontWeight: 600, width: '160px' }}>
                    Date <span className="text-danger">*</span>
                  </th>
                  <th style={{ padding: '0.5rem 0.625rem', textAlign: 'left', fontWeight: 600, width: '80px' }}>
                    Day
                  </th>
                  <th style={{ padding: '0.5rem 0.625rem', textAlign: 'left', fontWeight: 600 }}>
                    Description <span className="text-danger">*</span>
                  </th>
                  <th style={{ padding: '0.5rem 0.625rem', textAlign: 'left', fontWeight: 600, width: '130px' }}>
                    Type
                  </th>
                  <th style={{ padding: '0.5rem 0.625rem', textAlign: 'center', fontWeight: 600, width: '50px' }}>
                    &nbsp;
                  </th>
                </tr>
              </thead>
              <tbody>
                {holidayRows.map((row) => {
                  const dayName = row.date
                    ? new Date(row.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })
                    : '-';
                  return (
                    <tr key={row.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '0.375rem 0.5rem' }}>
                        <input
                          type="date"
                          className={`form-input${fieldErrors[`${row.id}-date`] ? ' form-field-error' : ''}`}
                          value={row.date}
                          disabled={isSaving}
                          onChange={(e) => updateRow(row.id, 'date', e.target.value)}
                          style={{ fontSize: '0.8125rem', padding: '0.25rem 0.375rem', width: '100%' }}
                        />
                        {fieldErrors[`${row.id}-date`] && (
                          <div className="form-error-message" style={{ fontSize: '0.7rem' }}>
                            {fieldErrors[`${row.id}-date`]}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.375rem 0.5rem', color: 'var(--text-secondary)' }}>
                        {dayName}
                      </td>
                      <td style={{ padding: '0.375rem 0.5rem' }}>
                        <input
                          type="text"
                          className={`form-input${fieldErrors[`${row.id}-description`] ? ' form-field-error' : ''}`}
                          value={row.description}
                          maxLength={200}
                          placeholder="Holiday name"
                          disabled={isSaving}
                          onChange={(e) => updateRow(row.id, 'description', e.target.value)}
                          style={{ fontSize: '0.8125rem', padding: '0.25rem 0.375rem', width: '100%' }}
                        />
                        {fieldErrors[`${row.id}-description`] && (
                          <div className="form-error-message" style={{ fontSize: '0.7rem' }}>
                            {fieldErrors[`${row.id}-description`]}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.375rem 0.5rem' }}>
                        <select
                          className="form-input"
                          value={row.holidayType}
                          disabled={isSaving}
                          onChange={(e) => updateRow(row.id, 'holidayType', e.target.value)}
                          style={{ fontSize: '0.8125rem', padding: '0.25rem 0.375rem', width: '100%' }}
                        >
                          {HOLIDAY_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '0.375rem 0.5rem', textAlign: 'center' }}>
                        <button
                          className="modal-header-btn"
                          onClick={() => removeRow(row.id)}
                          disabled={isSaving || holidayRows.length <= 1}
                          title="Remove row"
                          style={{ padding: '0.125rem' }}
                        >
                          <IconSystem.x size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Add Row button */}
          <button
            className="btn btn-secondary"
            onClick={addRow}
            disabled={isSaving}
            style={{ marginTop: '0.5rem', fontSize: '0.8125rem' }}
          >
            <IconSystem.add size={14} /> Add Row
          </button>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSaveAll} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save All'}
          </button>
        </div>
      </div>
    </>
  );
};

export default HolidayBulkAddModal;
